import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Users, Globe, Briefcase, ClipboardList, Wallet, AlertTriangle, TrendingUp, LifeBuoy, ArrowUpRight, Sparkles, GraduationCap, Clock, CalendarDays, UserPlus, Video, ExternalLink, Circle, BarChart3 } from "lucide-react";
import Avatar from "@/components/Avatar";

const fmt = (n) => new Intl.NumberFormat("tr-TR").format(n ?? 0);
const tl = (n) => `${fmt(Math.round((n ?? 0) * 100) / 100)} ₺`;

function StatCard({ label, value, hint, icon: Icon, accent = "default", testid, onClick }) {
  const aClass = { default: "text-[#0F172A]", success: "text-[#10B981]", danger: "text-[#EF4444]", indigo: "text-[#4F46E5]" }[accent] || "text-[#0F172A]";
  return (
    <button onClick={onClick} className="ky-card p-5 text-left ky-card-hover" data-testid={testid}>
      <div className="flex items-center justify-between text-[#64748B] mb-2.5">
        <span className="text-[11px] font-medium uppercase tracking-wider">{label}</span>
        {Icon && <Icon size={14} />}
      </div>
      <div className={`text-2xl font-semibold tabular-nums ${aClass}`}>{value}</div>
      {hint && <div className="text-[11px] text-[#94A3B8] mt-1.5">{hint}</div>}
    </button>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [stats, setStats] = useState(null);
  const [v3, setV3] = useState(null);
  const [insights, setInsights] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [activity, setActivity] = useState([]);
  const [team, setTeam] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    Promise.all([
      apiClient.get("/dashboard/stats").then((r) => r.data),
      apiClient.get("/dashboard/v3-stats").then((r) => r.data).catch(() => null),
      apiClient.get("/ai/insights").then((r) => r.data),
      apiClient.get(isAdmin ? "/tasks" : "/tasks?mine=true").then(r => r.data),
      apiClient.get("/dashboard/activity").then(r => r.data),
      isAdmin ? apiClient.get("/team/workload").then(r => r.data) : Promise.resolve([]),
    ]).then(([s, v, i, t, a, tm]) => { setStats(s); setV3(v); setInsights(i); setTasks(t || []); setActivity(a || []); setTeam(tm); });
  }, [isAdmin]);

  const upcoming = tasks.filter(t => t.status !== "tamamlandi").sort((a, b) => (a.due_date || "z").localeCompare(b.due_date || "z")).slice(0, 6);
  const revData = (stats?.revenue_by_month || []).slice(-6);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <div className="text-xs uppercase tracking-wider text-[#94A3B8] font-semibold">{new Date().toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long" })}</div>
        <h1 className="text-2xl font-bold mt-1">Hoş geldin {user?.name?.split(" ")[0]} 👋</h1>
        <p className="text-sm text-[#64748B] mt-1">Bugün {stats?.critical_tasks ?? 0} kritik konuyu öne aldım. Aşağıdan başlayabilirsin.</p>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard testid="stat-customers" label="Müşteri" value={fmt(stats?.customers)} icon={Users} hint="Aktif kayıt" onClick={() => nav("/musteriler")} />
        <StatCard testid="stat-services" label="Aktif Hizmet" value={fmt(stats?.services_active)} icon={Briefcase} hint={`${fmt(stats?.services_completed || 0)} tamamlandı`} onClick={() => nav("/hizmetler")} />
        <StatCard testid="stat-tasks" label="Açık Görev" value={fmt(stats?.open_tasks)} icon={ClipboardList} hint={`${stats?.critical_tasks || 0} kritik`} accent={stats?.critical_tasks ? "danger" : "default"} onClick={() => nav("/gorevler")} />
        <StatCard testid="stat-domains" label="Domain Uyarısı" value={fmt((stats?.domains_expiring_soon || 0) + (stats?.domains_expired || 0))} icon={Globe} hint={`${stats?.domains_expired || 0} dolmuş`} accent={(stats?.domains_expired || 0) > 0 ? "danger" : "default"} onClick={() => nav("/domainler")} />
        {isAdmin && <StatCard testid="stat-revenue" label="Tahsil Edilen" value={tl(stats?.total_revenue)} icon={Wallet} hint="Toplam gelir" accent="success" />}
        {isAdmin && <StatCard testid="stat-pending" label="Bekleyen Tahsilat" value={tl(stats?.pending_revenue)} icon={TrendingUp} hint="Açık bakiye" accent="indigo" />}
        <StatCard testid="stat-students" label="Öğrenci" value={fmt(stats?.students_total)} icon={GraduationCap} hint="Toplam" onClick={() => nav("/ogrenciler")} />
        <StatCard testid="stat-tickets" label="Açık Destek" value={fmt(stats?.open_tickets)} icon={LifeBuoy} hint="Müşteri talepleri" accent={(stats?.open_tickets || 0) > 0 ? "indigo" : "default"} onClick={() => nav("/destek")} />
      </div>

      {/* v3 widgets: finance + leads + meetings */}
      {v3 && (
        <div className="grid lg:grid-cols-3 gap-5">
          {/* This month finance (admin) */}
          {isAdmin && v3.finance_month && (
            <div className="ky-card p-5" data-testid="finance-month-card">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2"><BarChart3 size={15} className="text-[#10B981]"/><h3 className="font-semibold text-sm">Bu Ay Finans</h3></div>
                <button onClick={()=>nav("/finans")} className="text-xs text-[#4F46E5] hover:underline">Detay →</button>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-[#64748B]">Gelir</span><span className="font-mono font-semibold text-[#10B981]">+{tl(v3.finance_month.gelir)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-[#64748B]">Gider</span><span className="font-mono font-semibold text-[#EF4444]">-{tl(v3.finance_month.gider)}</span></div>
                <div className="border-t border-[#ECECEF] pt-2 flex justify-between"><span className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Net</span><span className={`font-mono text-lg font-bold tabular-nums ${v3.finance_month.net >= 0 ? "text-[#10B981]":"text-[#EF4444]"}`}>{tl(v3.finance_month.net)}</span></div>
              </div>
            </div>
          )}

          {/* Leads pipeline */}
          {v3.leads_count != null && (
            <div className="ky-card p-5" data-testid="leads-pipeline-card">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2"><UserPlus size={15} className="text-[#4F46E5]"/><h3 className="font-semibold text-sm">Aktif Pipeline</h3></div>
                <button onClick={()=>nav("/toplantilar")} className="text-xs text-[#4F46E5] hover:underline">Lead Kanban →</button>
              </div>
              <div className="text-3xl font-bold tabular-nums">{v3.leads_count}<span className="text-sm font-normal text-[#94A3B8] ml-1">lead</span></div>
              {v3.leads_pipeline_value != null && <div className="text-sm text-[#10B981] font-mono mt-1">≈ {tl(v3.leads_pipeline_value)}</div>}
              <div className="mt-3 space-y-1.5">
                {(v3.top_leads || []).slice(0,3).map(l => (
                  <div key={l.id} className="flex items-center justify-between text-xs">
                    <span className="truncate flex-1">{l.name} {l.company && <span className="text-[#94A3B8]">· {l.company}</span>}</span>
                    {l.estimated_value != null && <span className="font-mono text-[#10B981] ml-2">{tl(l.estimated_value)}</span>}
                  </div>
                ))}
                {(!v3.top_leads || v3.top_leads.length === 0) && <div className="text-xs text-[#94A3B8]">Henüz lead yok.</div>}
              </div>
            </div>
          )}

          {/* Upcoming meetings */}
          <div className="ky-card p-5" data-testid="meetings-week-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><CalendarDays size={15} className="text-[#7C3AED]"/><h3 className="font-semibold text-sm">7 Gün İçinde</h3></div>
              <button onClick={()=>nav("/takvim")} className="text-xs text-[#4F46E5] hover:underline">Takvim →</button>
            </div>
            <div className="space-y-2 max-h-[180px] overflow-y-auto">
              {(v3.upcoming_meetings || []).length === 0 && (v3.upcoming_lessons || []).length === 0 && <div className="text-xs text-[#94A3B8] text-center py-4">Bu hafta planlı bir şey yok</div>}
              {(v3.upcoming_meetings || []).map(m => (
                <button key={m.id} onClick={()=>nav("/toplantilar")} className="w-full text-left flex items-start gap-2 p-2 rounded-md hover:bg-[#FAFAFB]">
                  <div className="w-1 h-8 bg-[#4F46E5] rounded-full shrink-0 mt-0.5"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">{m.title}</div>
                    <div className="text-[10px] text-[#94A3B8] font-mono">{new Date(m.start_at).toLocaleString("tr-TR",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</div>
                  </div>
                  {m.meeting_url && <Video size={12} className="text-[#10B981] shrink-0"/>}
                </button>
              ))}
              {(v3.upcoming_lessons || []).map(l => (
                <button key={l.id} onClick={()=>nav("/ogrenciler")} className="w-full text-left flex items-start gap-2 p-2 rounded-md hover:bg-[#FAFAFB]">
                  <div className="w-1 h-8 bg-[#10B981] rounded-full shrink-0 mt-0.5"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">📚 {l.name}</div>
                    <div className="text-[10px] text-[#94A3B8] font-mono">{new Date(l.next_lesson_date).toLocaleString("tr-TR",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Online team strip */}
      {v3?.online_team && v3.online_team.length > 0 && (
        <div className="ky-card p-3 flex items-center gap-3" data-testid="online-team-strip">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-[#10B981] uppercase tracking-wider"><Circle size={6} className="fill-current"/>Çevrimiçi</div>
          <div className="flex items-center gap-3 flex-wrap">
            {v3.online_team.map(u => (
              <div key={u.id} className="flex items-center gap-1.5 text-xs"><Avatar name={u.name} color={u.avatar_color} size={20}/><span>{u.name}</span></div>
            ))}
          </div>
        </div>
      )}

      {/* Main 2-column area */}
      <div className="grid lg:grid-cols-3 gap-5">
        {/* AI Insights */}
        <div className="lg:col-span-2 ky-card p-5" data-testid="insights-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-[#4F46E5]" />
              <h3 className="font-semibold text-[15px]">Akıllı Öncelikler</h3>
              <span className="ky-badge ky-badge-indigo">{insights.length}</span>
            </div>
          </div>
          <div className="space-y-2 max-h-[420px] overflow-y-auto">
            {insights.length === 0 && <div className="text-sm text-[#94A3B8] text-center py-10">Şu an kritik uyarı yok.</div>}
            {insights.map((ins, i) => (
              <button key={i} onClick={() => ins.link && nav(ins.link)} className={`w-full text-left flex items-start gap-3 px-3 py-2.5 rounded-lg border ${ins.level === "kritik" ? "border-[#FECACA] bg-[#FEF2F2]" : "border-[#FDE68A] bg-[#FFFBEB]"} hover:border-opacity-80`} data-testid={`insight-${i}`}>
                <AlertTriangle size={14} className={`shrink-0 mt-0.5 ${ins.level === "kritik" ? "text-[#DC2626]" : "text-[#D97706]"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-[#94A3B8] mb-0.5">{ins.type} · {ins.level}</div>
                  <div className="text-sm font-medium">{ins.title}</div>
                  <div className="text-xs text-[#64748B] mt-0.5">{ins.message}</div>
                </div>
                <ArrowUpRight size={14} className="text-[#94A3B8] mt-1" />
              </button>
            ))}
          </div>
        </div>

        {/* Upcoming tasks */}
        <div className="ky-card p-5" data-testid="upcoming-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-[15px]">Yaklaşan Görevler</h3>
            <button onClick={() => nav("/gorevler")} className="text-xs text-[#4F46E5] hover:underline">Tümü →</button>
          </div>
          <div className="space-y-2">
            {upcoming.length === 0 && <div className="text-sm text-[#94A3B8] text-center py-6">Bekleyen görev yok 🎉</div>}
            {upcoming.map((t) => (
              <div key={t.id} className="flex items-start gap-2 p-2 rounded-md hover:bg-[#FAFAFB]">
                <div className={`mt-1 w-1.5 h-1.5 rounded-full ${t.priority === "kritik" ? "bg-[#EF4444]" : t.priority === "yuksek" ? "bg-[#F59E0B]" : "bg-[#94A3B8]"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium truncate">{t.title}</div>
                  <div className="text-[11px] text-[#94A3B8] mt-0.5 flex items-center gap-2">
                    {t.due_date && <><Clock size={10} /> {new Date(t.due_date).toLocaleDateString("tr-TR")}</>}
                    <span className="ky-badge ky-badge-neutral">{t.status?.replace("_", " ")}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts row */}
      {isAdmin && (
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 ky-card p-5">
            <h3 className="font-semibold text-[15px] mb-4">Gelir Trendi (Son 6 Ay)</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                <AreaChart data={revData}>
                  <defs>
                    <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#4F46E5" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#ECECEF" strokeDasharray="3 3" />
                  <XAxis dataKey="month" stroke="#94A3B8" fontSize={11} />
                  <YAxis stroke="#94A3B8" fontSize={11} />
                  <Tooltip contentStyle={{ background: "white", border: "1px solid #ECECEF", borderRadius: 8 }} formatter={(v) => tl(v)} />
                  <Area type="monotone" dataKey="amount" stroke="#4F46E5" strokeWidth={2} fill="url(#rev)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="ky-card p-5" data-testid="workload-card">
            <h3 className="font-semibold text-[15px] mb-4">Ekip İş Yükü</h3>
            <div className="space-y-3">
              {team.slice(0, 6).map((t) => {
                const total = (t.open_tasks || 0) + (t.active_services || 0);
                const pct = Math.min(100, total * 10);
                return (
                  <div key={t.id} className="flex items-center gap-3">
                    <Avatar name={t.name} color={t.avatar_color} size={28} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium truncate">{t.name}</div>
                      <div className="h-1.5 bg-[#F4F4F5] rounded-full mt-1 overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#4F46E5] to-[#7C3AED]" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <div className="text-xs text-[#64748B] tabular-nums">{t.open_tasks} / {t.active_services}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* AI Tools Quick Access */}
      <div className="ky-card p-5 bg-gradient-to-br from-[#4F46E5] via-[#5B21B6] to-[#7C3AED] text-white relative overflow-hidden" data-testid="dash-ai-quick">
        <div className="absolute top-0 right-0 w-60 h-60 bg-white/5 rounded-full blur-3xl -mt-20 -mr-20"/>
        <div className="relative z-10 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/15 backdrop-blur-sm flex items-center justify-center"><Sparkles size={18}/></div>
            <div>
              <div className="text-xs uppercase tracking-wider text-white/70 font-semibold">Gemini 2.5 Flash</div>
              <h3 className="font-bold text-lg">AI Araçlarına Hızlı Erişim</h3>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={()=>nav("/ai-araclar/anahtar-kelime")} className="px-3 py-1.5 bg-white text-[#4F46E5] rounded-md text-xs font-medium hover:bg-white/90 flex items-center gap-1.5" data-testid="dash-ai-kw"><BarChart3 size={12}/>Anahtar Kelime</button>
            <button onClick={()=>nav("/ai-araclar/reklam-metni")} className="px-3 py-1.5 bg-white text-[#4F46E5] rounded-md text-xs font-medium hover:bg-white/90 flex items-center gap-1.5"><Sparkles size={12}/>Reklam Metni</button>
            <button onClick={()=>nav("/mesajlar")} className="px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-md text-xs font-medium hover:bg-white/20 flex items-center gap-1.5">AI Asistan'a Sor →</button>
          </div>
        </div>
      </div>

      {/* Activity feed */}
      <div className="ky-card p-5">
        <h3 className="font-semibold text-[15px] mb-4">Son Aktiviteler</h3>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {activity.length === 0 && <div className="text-sm text-[#94A3B8] text-center py-6">Henüz aktivite yok.</div>}
          {activity.map((a) => (
            <div key={a.id} className="flex items-start gap-3 py-2 border-b border-[#ECECEF] last:border-0">
              <div className="w-1.5 h-1.5 rounded-full bg-[#4F46E5] mt-2" />
              <div className="flex-1 text-sm">{a.text}</div>
              <div className="text-[11px] text-[#94A3B8]">{new Date(a.created_at).toLocaleString("tr-TR")}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

